import { useQuery } from "@tanstack/react-query";
import { millisecondsInMinute } from "date-fns";
import { MIGRATION_STATE_QUERY_KEY, apiGateway, useAuth } from "../context";

/**
 * Where a migrating doctor stands in their move to a new signing certificate:
 * listed for the move, signing as before, without the new certificate yet.
 */
export interface MigrationState {
  /** ISO date-time; null never blocks. */
  validationDeadline: string | null;
  validated: boolean;
  /** Set when the new certificate could not be created with their PIN. */
  secondaryCertificateError: string | null;
  /** Past the deadline without validating: cannot sign until they validate. */
  signingBlocked: boolean;
}

interface MigrationStateResponse {
  /** Null for anyone who is not migrating. */
  migrationState: MigrationState | null;
}

/**
 * `data` is undefined while loading and when the request fails: callers treat
 * both as "nothing to show", and the signing endpoints keep their own check.
 */
export const useMigrationState = () => {
  const { isUserAuthed } = useAuth();

  return useQuery({
    queryKey: MIGRATION_STATE_QUERY_KEY,
    queryFn: () =>
      apiGateway<MigrationStateResponse>({
        url: "/enrolment/migration-state",
      }),
    select: (data) => data.migrationState,
    enabled: isUserAuthed,
    staleTime: 5 * millisecondsInMinute,
    retry: false,
  });
};
